import axios from 'axios';
import betterWmsLayer from 'utils/leafletUtils/betterWmsLayer';
import dayjs from "dayjs";

const API_URL = process.env.REACT_APP_API_URL; // Address to the backend

export const getEumetsatLayer = async (layerOpacity, provider, year, month, day) => {

    const url = `${API_URL}/api/eumetsat/layer`

    let newLayer;
    try {

        newLayer = await new betterWmsLayer(url, {
            provider: provider,
            year: year,
            month: month,
            day: day,

            transparent: true, // Only these variables below are part of the WMS request.
            version: "1.3.0",
            format: "image/png",
            opacity: layerOpacity / 100
        });

    } catch (error) {
        console.error('Error fetching EUMETSAT data: ', error);
    }

    return newLayer;

}

// Function to get common dates for multiple folders
const getCommonDates = async (foldersParams, setErrorMessage) => {

    const cacheKey = 'DWDates';
    const today = new dayjs();
    const cachedData = JSON.parse(localStorage.getItem(cacheKey));

    try {

        if (cachedData && dayjs(cachedData.date).isAfter(today)) {
            const returnedData = findCommonDates(cachedData.allDates, foldersParams);
            return returnedData;
        }

        const response = await axios.post(`${API_URL}/api/dates`, foldersParams, { withCredentials: true });

        localStorage.setItem(cacheKey, JSON.stringify({
            date: today.clone().add(2, 'hour'),
            allDates: response.data
        }));

        const returnedData = findCommonDates(response.data, foldersParams);
        return returnedData;
    } catch (error) {
        const errorMessage = error.response?.data?.message || error.message || "Unknown error occurred";
        setErrorMessage(errorMessage);

        console.error(errorMessage);
        throw error;

    }
};

const getCommonDatesDefault = async () => {
    const cacheKey = 'DWDates';
    const today = dayjs();
    const cachedData = JSON.parse(localStorage.getItem(cacheKey));

    try {
        if (cachedData && dayjs(cachedData.date).isAfter(today)) {
            return;
        }

        const response = await axios.post(`${API_URL}/api/dates`, {}, { withCredentials: true });

        if (!response.data || response.data.length === 0) {
            throw new Error('No date data returned from API.');
        }

        localStorage.setItem(cacheKey, JSON.stringify({
            date: today.clone().add(2, 'hour'),
            allDates: response.data
        }));

    } catch (error) {
        const errorMessage = error.response?.data?.message || error.message || 'Unknown error occurred while fetching dates';
        console.error(errorMessage);
        throw new Error(errorMessage);
    }
};

const findCommonDates = (dataJson, foldersParams) => {
    // Initialize an array to store matching dates for each folder
    let allMatchingDates = [];
    foldersParams.forEach(folderParam => {
        const folderName = folderParam.folder.toLowerCase(); // Convert folderName to lowercase
        const timeResolution = folderParam.timeResolution;
        const spaceResolution = folderParam.spaceResolution;

        // Check if dataJson contains the folder (normalize case)
        let folderFound = false;
        for (let folder in dataJson) {
            if (dataJson.hasOwnProperty(folder) && folder.toLowerCase() === folderName) {
                folderFound = true;

                const data = dataJson[folder];

                // Initialize an array to store matching dates for the current folder
                let matchingDates = [];

                // Loop through each key in data (e.g., drought impacts, vegetation condition, etc.)
                for (let key in data) {
                    if (data.hasOwnProperty(key)) {

                        // Ensure the data[key] is a string
                        if (typeof data[key] === "string") {
                            // Check if the string contains both timeResolution and spaceResolution
                            const isMatch = data[key].includes(timeResolution) && data[key].includes(spaceResolution);
                            if (isMatch) {

                                // Extract the year, month, and day from the string (assuming format "5km\\12wk\\YYYY\\MM\\DD")
                                const parts = data[key].split('\\');
                                const year = parts[2];
                                const month = parts[3].padStart(2, '0'); // Ensure month is two digits
                                const day = parts[4].padStart(2, '0'); // Ensure day is two digits

                                // Format the date as YYYY-MM-DD
                                const formattedDate = `${year}-${month}-${day}`;

                                // Add the formatted date to the array
                                matchingDates.push(formattedDate);
                            }
                        } else {
                            console.log(`Skipping key "${key}" because it's not a string`);
                        }
                    }
                }

                // Store the matching dates for this folder.
                allMatchingDates.push(matchingDates);
                break; // Found the folder, no need to check further.
            }
        }

        // If no matching folder found in dataJson, log a message.
        if (!folderFound) {
            console.log(`No data found for folder: ${folderName}`);
        }
    });

    // Find common dates across all folders
    const commonDates = allMatchingDates.reduce((acc, dates, index) => {
        if (acc.length === 0) {
            return dates; // Start with the dates from the first folder
        }
        // Find intersection of previous common dates and current folder dates
        const intersection = acc.filter(date => dates.includes(date));
        return intersection;
    }, []);
    return commonDates;
};

export const getZonalStatistics = async (mapParametersFull, file_paths, nutsId, nutsFile, mapserver_url, layers, no_data_values, bbox) => {
    try {

        const ensureArray = (item) => Array.isArray(item) ? item : [item];

        file_paths = ensureArray(file_paths);
        layers = ensureArray(layers);
        no_data_values = ensureArray(no_data_values);

        // parameters for multiple layers
        const params = {
            'file_path[]': file_paths,
            nutsId,
            nutsFile,
            mapserver_url,
            'layer[]': layers,
            'no_data[]': no_data_values,
            bbox
        };

        params['providers'] = mapParametersFull.map(p => p.provider);
        params['timeResolutions'] = mapParametersFull.map(p => p.timeResolution);
        params['spaceResolutions'] = mapParametersFull.map(p => p.spaceResolution);
        params['mapFilenames'] = mapParametersFull.map(p => p.mapFilename);
        params['filenames'] = mapParametersFull.map(p => p.filename);
        params['dateStrings'] = mapParametersFull.map(p => p.dateString);
        params['years'] = mapParametersFull.map(p => p.year);
        params['months'] = mapParametersFull.map(p => p.month);
        params['days'] = mapParametersFull.map(p => p.day);
        params['predictionDates'] = mapParametersFull.map(p => p.predictionDate);

        const response = await axios.get(`${API_URL}/api/zonal`, {
            params,
            withCredentials: true
        });

        const zonalStatisticsData = response.data;
        return zonalStatisticsData;
    } catch (error) {
        console.error('Error fetching zonal statistics:', error);
        throw error;
    }
};

const getAnimation = async (spaceResolution, timeResolution, startDate, endDate, provider, mapRef, bbox, fileName) => {
    try {
        const bounds = [
            bbox.southWest.x, // west
            bbox.southWest.y, // south
            bbox.northEast.x, // east
            bbox.northEast.y  // north
        ];

        const response = await axios.get(`${API_URL}/api/animation`, {//make a request to the backend for the resolutions and folder to search in
            params: {
                spaceResolution: spaceResolution,
                timeResolution: timeResolution,
                layer: provider,
                startDate: startDate.format("YYYY-MM-DD"),
                endDate: endDate.format("YYYY-MM-DD"),
                bounds: bounds,
                mapfile: fileName
            },
            withCredentials: true
        }
        );
        const animation = response.data;// the data received

        return animation;

    } catch (error) { // If we cannot get the needed data, print an error.
        //console.error('Error fetching data:', error);
        //throw error;
        console.error(error.response.data.message);
    }
};

const getRender = async (spaceResolution, timeResolution, startDate, endDate, provider, mapRef, bbox, fileName) => {
    try {

        const bounds = [
            bbox.southWest.x, // west
            bbox.southWest.y, // south
            bbox.northEast.x, // east
            bbox.northEast.y  // north
        ];

        const response = await axios.get(`${API_URL}/api/render`, { // Make a request to the backend for the resolutions and folder to search in.
            params: {
                spaceResolution: spaceResolution,
                timeResolution: timeResolution,
                layer: provider,
                startDate: startDate.format("YYYY-MM-DD"),
                endDate: endDate.format("YYYY-MM-DD"),
                bounds: bounds,
                mapfile: fileName
            },
            withCredentials: true
        }
        );
        const zonalStatisticData = response.data;// the data received
        return zonalStatisticData;

    } catch (error) {//if we cannot get the needed data print an error
        //console.error('Error fetching data:', error);
        //throw error;
        console.error(error.response.data.message);
    }
};

const homeService = { // To run the function.
    getCommonDates,
    getZonalStatistics,
    getAnimation,
    getRender,
    getCommonDatesDefault
};

export default homeService;

